
import './ImportCmsCategories.scss';

import React, { FC, useContext, useState } from 'react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { useTokenExpiredHandler } from 'customHooks/useTokenExpiredHandler';
import { Entities } from 'models/Entities';
import { LogLevel } from 'models/LogLevel';

import { useEntitySetupHandler } from '../SetupWizard/useEntitySetupHandler';
import { WizardStepInfo } from '../SetupWizard/WizardStepInfo';

interface ImportCmsCategoriesProps extends WizardStepInfo {
  cachedProjectId: number | undefined;
  cachedCmsConnectionId: number | undefined;
}

const ImportCmsCategories: FC<ImportCmsCategoriesProps> = ({
  cachedProjectId,
  cachedCmsConnectionId,
  ...wizardContext
}) => {
  let importedCategoriesResponse: Response;
  const { configRepo, notify, sendLog } = useContext(AppContext);
  const onTokenExpired = useTokenExpiredHandler();
  const [btnClicked, setBtnClicked] = useState(false);

  const cmsCategoriesHandler = useEntitySetupHandler(
    configRepo!.PUBLISH_API_BASE_URI,
    sendLog,
    onTokenExpired,
    () => { },
    Entities.CmsCategories,
  );

  const importCategories = async () => {
    importedCategoriesResponse = await cmsCategoriesHandler.executeAction(
      `Import?projectId=${cachedProjectId}`,
    );
    if (!importedCategoriesResponse.ok) {
      let message = "";
      switch (importedCategoriesResponse.status) {
        case 404:
          message = `Failed to connect to your CMS.  Your Project's website URL may be incorrect.`;
          break;
        case 400:
          message = `Failed to connect to your CMS.  Your CMS Connection's username may be incorrect.`;
          break;
        case 401:
          message = `Failed to connect to your CMS.  Your CMS Connection's application password may be incorrect.`;
          break;
        default:
          message = `Failed to import category information from your CMS.  Try updating your Project's website url, or CMS Connection's username and application password, then try again.`;
          break;
      }
      notify(
        message,
        NotificationType.Error,
      );
      sendLog(`CMS import error: ${message}, project: ${cachedProjectId}`, LogLevel.warn);

      wizardContext.previousStep();
    } else {
      wizardContext.nextStep();
    }
  };

  const onImportClick = async () => {
    if (!cachedProjectId || !cachedCmsConnectionId || wizardContext.currentStep !== 4) {
      return;
    }

    setBtnClicked(true);

    await importCategories()
      .catch((e) => {
        sendLog(`CMS import error: ${e.message}, project: ${cachedProjectId}`, LogLevel.error, e);
      })
      .finally(() => {
        setBtnClicked(false);
      })

  };

  return (
    <div className="ImportCmsCategories mb-4 row">
      {(btnClicked) ?
        <div className='col-sm-10 offset-sm-2'>
          <h1 className="wizard-title text-center">Connecting to Your CMS...</h1>
          <p>
            Please wait while we connect with your CMS. Upon completion you will automatically be taken
            to the next step.
          </p>
        </div>
        :
        <div className='col-sm-10 offset-sm-2'>
          <h1 className="wizard-title text-center">Test CMS Connection</h1>
          <p>
            Please click the button below to begin importing your blog categories from your CMS!
          </p>
          <div className="wizard-actions">
            <div className="nav">
              <div className="nav-item">
                <button type="button" className="btn btn-secondary btn-block" id="importPreviousBtn" onClick={wizardContext.previousStep}>Previous</button>
              </div>
              <div className="ml-2 nav-item">
                <button type='button' onClick={onImportClick} className='btn btn-primary' id="importCmsCategories">Import Categories</button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default ImportCmsCategories;
